/**
 * A Turkish phone number as it is said — "0532 415 88 21" — however it arrived:
 * a marketplace sends "5324158821", a seller types "05324158821" or
 * "+90 (532) 415-88-21". The one function every app writes a phone with
 * (docs/15 §3), handed on by each app's `lib/format`.
 *
 * Only a number that is certainly Turkish is rewritten. A foreign number, a
 * 444 line or one with an extension is shown as typed: rewritten half-way, it
 * would read as a Turkish number that nobody answers.
 */

// What people write between the digits. A letter, "#" or "x" is an extension
// or not a number at all, so it leaves the value as typed.
const SEPARATORS = /[\s().\-/]/g;

// Ten significant digits: a line (2–4), a mobile (5), 0800/0850 (8), 0900 (9).
const NATIONAL = /^[2-589]\d{9}$/;

/** The ten significant digits of a Turkish number — "5324158821" — or null when it is not certainly one. */
export function turkishPhoneDigits(value: string | null | undefined): string | null {
    const compact = (value ?? '').trim().replace(SEPARATORS, '');
    let digits: string;
    const international = /^(?:\+|00)(\d+)$/.exec(compact);
    if (international) {
        // A country code is written, so it must be Turkey's.
        const number = international[1] ?? '';
        if (!number.startsWith('90')) return null;
        digits = number.slice(2);
    } else if (/^\d+$/.test(compact)) {
        digits = compact.length === 12 && compact.startsWith('90') ? compact.slice(2) : compact;
    } else {
        return null;
    }
    // The trunk zero: "05324158821", and "+90 0532…" as some people write it.
    if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
    return NATIONAL.test(digits) ? digits : null;
}

/** "0532 415 88 21" for a Turkish number; anything else as typed, and "" for nothing. */
export function formatPhone(value: string | null | undefined): string {
    const digits = turkishPhoneDigits(value);
    if (!digits) return (value ?? '').trim();
    return `0${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
}
